import { useState, type ReactNode } from 'react';
import {
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

export type CollaborationTaskStatus = 'assigned' | 'in_progress' | 'completed' | 'expired';
export type CollaborationTaskPriority = 'high' | 'normal' | 'low';

export interface AssociatedTaskViewItem {
  id: string;
  subject: string;
  priority: CollaborationTaskPriority;
  status: CollaborationTaskStatus;
  dueDate: string;
  assigneeLabel?: string;
  assigneeActive?: boolean;
  creatorLabel?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AssociatedTasksViewProps {
  title: string;
  tasks: AssociatedTaskViewItem[];
  isLoading?: boolean;
  loadError?: string;
  permissionMessage?: string;
  onRetry?: () => void;
  onCreate?: () => void;
  createLabel?: string;
  renderTaskActions?: (task: AssociatedTaskViewItem) => ReactNode;
  createDialog?: ReactNode;
  loadingLabel?: string;
}

const isActive = (task: AssociatedTaskViewItem) =>
  task.status === 'assigned' || task.status === 'in_progress';

export default function AssociatedTasksView({
  title,
  tasks,
  isLoading = false,
  loadError,
  permissionMessage,
  onRetry,
  onCreate,
  createLabel = 'Create task',
  renderTaskActions,
  createDialog,
  loadingLabel = 'Loading live tasks...',
}: AssociatedTasksViewProps) {
  const [filter, setFilter] = useState<'active' | 'closed'>('active');
  const activeTasks = tasks.filter(isActive);
  const closedTasks = tasks.filter((task) => !isActive(task));
  const filteredTasks = filter === 'active' ? activeTasks : closedTasks;

  return (
    <section
      aria-labelledby="associated-tasks-heading"
      className="relative rounded-xl border border-brand-border bg-white p-5 shadow-sm"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardDocumentCheckIcon className="h-5 w-5 text-purple-600" aria-hidden="true" />
          <h3
            id="associated-tasks-heading"
            className="text-sm font-semibold uppercase tracking-wide text-brand-text-primary"
          >
            {title}
          </h3>
        </div>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            className="rounded-full p-1 text-gray-500 transition-colors hover:bg-gray-100"
            title={createLabel}
            aria-label={createLabel}
          >
            <PlusIcon className="h-5 w-5 text-purple-600" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="mb-3 flex border-b border-gray-100 text-xs">
        <button
          type="button"
          className={`pb-2 pr-3 font-medium ${
            filter === 'active'
              ? 'border-b-2 border-purple-600 text-purple-600'
              : 'text-gray-400'
          }`}
          onClick={() => setFilter('active')}
        >
          Active ({activeTasks.length})
        </button>
        <button
          type="button"
          className={`px-3 pb-2 font-medium ${
            filter === 'closed'
              ? 'border-b-2 border-purple-600 text-purple-600'
              : 'text-gray-400'
          }`}
          onClick={() => setFilter('closed')}
        >
          Closed ({closedTasks.length})
        </button>
      </div>

      <h4 className="sr-only">
        {filter === 'active'
          ? `Active tasks (${activeTasks.length})`
          : `Closed tasks (${closedTasks.length})`}
      </h4>

      {permissionMessage && (
        <p className="rounded-lg border border-dashed border-brand-border bg-brand-surface p-3 text-sm text-brand-text-secondary">
          {permissionMessage}
        </p>
      )}

      {isLoading && <p className="py-4 text-center text-xs text-gray-400">{loadingLabel}</p>}

      {loadError && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-800">{loadError}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-800"
            >
              Retry associated tasks
            </button>
          )}
        </div>
      )}

      {!permissionMessage && !isLoading && !loadError && filteredTasks.length === 0 && (
        <p className="py-4 text-center text-xs text-gray-400">No tasks in this stage.</p>
      )}

      <div className="max-h-[30rem] space-y-3 overflow-y-auto">
        {filteredTasks.map((task) => (
          <article
            key={task.id}
            aria-label={`Task ${task.subject}`}
            className="rounded-lg border border-gray-100 bg-gray-50 p-3 transition-all hover:border-purple-200"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">{task.subject}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span className={`rounded px-1 font-semibold lowercase ${
                    task.priority === 'high'
                      ? 'bg-red-50 text-red-600'
                      : 'bg-gray-200/60 text-gray-600'
                  }`}>
                    {task.priority}
                  </span>
                  <span>Due {task.dueDate}</span>
                  <span className="capitalize">{task.status.replace('_', ' ')}</span>
                </div>
                {(task.assigneeLabel || task.creatorLabel) && (
                  <dl className="mt-2 grid gap-1 text-xs text-gray-500 sm:grid-cols-2">
                    {task.assigneeLabel && (
                      <div>
                        <dt className="inline font-medium">Assignee: </dt>
                        <dd className="inline">
                          {task.assigneeLabel}
                          {task.assigneeActive === false && (
                            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
                              Inactive assignee
                            </span>
                          )}
                        </dd>
                      </div>
                    )}
                    {task.creatorLabel && (
                      <div>
                        <dt className="inline font-medium">Creator: </dt>
                        <dd className="inline">{task.creatorLabel}</dd>
                      </div>
                    )}
                  </dl>
                )}
                {(task.createdAt || task.updatedAt) && (
                  <p className="mt-2 text-xs text-gray-400">
                    {task.createdAt && `Created ${new Date(task.createdAt).toLocaleString()}`}
                    {task.createdAt && task.updatedAt && ' · '}
                    {task.updatedAt && `Updated ${new Date(task.updatedAt).toLocaleString()}`}
                  </p>
                )}
              </div>
              {!renderTaskActions && (
                <ChevronRightIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
              )}
            </div>
            {renderTaskActions && (
              <div className="mt-3 border-t border-gray-200 pt-3">
                {renderTaskActions(task)}
              </div>
            )}
          </article>
        ))}
      </div>
      {createDialog}
    </section>
  );
}
